import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Bus, ArrowLeft, Building2, User, Mail, Phone, 
  MapPin, FileText, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    registration_number: '',
    contact_person: '',
    email: '',
    phone: '',
    address: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to register as a bus operator',
        variant: 'destructive'
      });
      navigate('/login', { state: { returnTo: '/bus/operator/apply' } });
      return;
    }

    // Validate
    if (!formData.company_name || !formData.registration_number || !formData.contact_person || !formData.email || !formData.phone) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/bus/operators/register', {
        ...formData,
        phone: `+260${formData.phone.replace(/\D/g, '')}`
      });
      setSubmitted(true);
      toast({
        title: 'Application Submitted!',
        description: 'We will review your application and get back to you',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit application',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Application Submitted!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your interest in becoming a Tekstar Bus operator. 
            Our team will review your application and contact you within 2-3 business days.
          </p>
          <div className="space-y-3">
            <Link to="/bus">
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                Back to Bus Booking
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Go to Homepage
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/bus">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Become a Bus Operator</h1>
              <p className="text-indigo-100">Join Tekstar and grow your business</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 -mt-4">
        {/* Benefits */}
        <Card className="p-4 mb-6 bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="font-semibold mb-3">Why Partner with Tekstar?</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Reach more passengers</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Easy booking management</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Real-time seat tracking</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Secure payments</span>
            </div>
          </div>
        </Card>

        {/* Application Form */}
        <Card className="p-6" data-testid="operator-application-form">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Company Information
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Name *
              </Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="e.g., Mazhandu Family Bus Services"
                className="mt-1"
                data-testid="company-name"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Business Registration Number *
              </Label>
              <Input
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                placeholder="e.g., REG123456"
                className="mt-1"
                data-testid="reg-number"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Contact Person Name *
              </Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Full name"
                className="mt-1"
                data-testid="contact-person"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="company@email.com"
                  className="mt-1"
                  data-testid="email"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number *
                </Label>
                <div className="flex mt-1">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                    placeholder="XXX XXX XXX"
                    maxLength={9}
                    type="tel"
                    className="rounded-l-none"
                    data-testid="phone"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Business Address
              </Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Physical address of your business"
                className="mt-1"
                data-testid="address"
              />
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">Required Documents</p>
                  <p className="text-yellow-700 mt-1">
                    After registration, you'll need to upload:
                  </p>
                  <ul className="list-disc list-inside text-yellow-700 mt-1">
                    <li>Operating License</li>
                    <li>Vehicle Insurance</li>
                    <li>Tax Clearance Certificate</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button 
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 py-6"
              disabled={loading}
              data-testid="submit-application"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Bus className="h-5 w-5 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-4">
          By submitting, you agree to our{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default BusOperatorApplication;
